import { useState } from 'react';
import { VOICE_PROFILES_UI_ENABLED } from '@utils/constants/app-constants.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CheckCircle2, Fingerprint, ShieldCheck, Trash2 } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useRoles } from '@utils/hooks/use-roles.ts';
import {
  useClearVoiceSamples,
  useDeleteVoiceSample,
  useGetVoiceSamples,
  useVoiceProfileCapabilities
} from '@utils/api/organizations/speakers/voice-profiles';
import { VoiceProfileSample } from '@utils/types/organization/voice-profile.ts';
import { formatDate } from '@utils/date.ts';
import { voiceErrorKey } from '@utils/editors/voice-profile-errors.ts';

interface Props {
  speakerId: number;
  speakerName: string;
}

/** Voice setup state for one directory speaker.
 *
 * Everyone looking at the form sees whether this person has a voice saved
 * ("No voice saved" or "Voice saved · N samples") and how to add one from a
 * recording. Only organization admins see the sample list and the
 * delete/clear controls — the backend hides the sample metadata from everyone
 * else, so the list cannot render below that line.
 *
 * Samples hold encrypted embeddings, never retained recordings, so there is
 * deliberately no playback — only labels, dates and consent metadata. Deleting
 * one sample and clearing all of them are separate actions with separate
 * confirmations: the second is destructive in a way the first is not. Deletion
 * keeps working when the feature is switched off, so an organization can
 * always withdraw data it has stored. */
export const VoiceSamplesManager = ({ speakerId, speakerName }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { isAdmin, isSysOp } = useRoles();

  const [pendingDelete, setPendingDelete] = useState<VoiceProfileSample | null>(
    null
  );
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // Edi's production gate: hide every voice control from users who shouldn't
  // see unfinished UI. In staging/dev the gate opens and the usual role
  // checks apply.
  const canManage = VOICE_PROFILES_UI_ENABLED && (isAdmin || isSysOp);
  // Capabilities drives the non-admin state line too (so a contributor sees
  // "Voice setup is managed by administrators…" when the feature is on).
  // Still gated behind the production flag so the extra request never fires
  // outside staging/dev.
  const { data: capabilities } = useVoiceProfileCapabilities(
    VOICE_PROFILES_UI_ENABLED
  );
  const samples = useGetVoiceSamples(speakerId, canManage);
  const deleteSample = useDeleteVoiceSample();
  const clearSamples = useClearVoiceSamples();

  // Production: nothing voice-related renders at all.
  if (!VOICE_PROFILES_UI_ENABLED) return null;

  // The whole feature is off for this organization and the user is not an
  // admin — nothing to show and nothing actionable to say.
  if (!canManage && !capabilities?.enabled) return null;

  // Non-admins see a static state line explaining who manages voice setup —
  // sample counts are admin-only on the backend, so there is no number to
  // show here, but the ticket is explicit that the form must explain what
  // voice setup is and how it is managed. */
  if (!canManage) {
    return (
      <section
        className="space-y-2 rounded-md border p-3"
        style={{ borderColor: getColour('border') }}
        aria-label={t('voice-samples')}
      >
        <span className="flex items-center gap-2">
          <Fingerprint size={14} aria-hidden />
          <Text as="span" size="sm" weight="medium">
            {t('voice-samples')}
          </Text>
        </span>
        <Text as="p" size="sm" tone="muted">
          {t('voice-managed-by-admins')}
        </Text>
      </section>
    );
  }

  const profiles = samples.data?.profiles ?? [];
  const count = samples.data?.count ?? 0;
  const maxSamples = capabilities?.max_samples_per_speaker ?? 20;

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteSample.mutateAsync({
        speakerId,
        profileId: pendingDelete.id
      });
      toast.success(t('voice-sample-deleted'));
    } catch (error) {
      toast.error(t(voiceErrorKey(error)));
    } finally {
      setPendingDelete(null);
    }
  };

  const handleClearAll = async () => {
    try {
      await clearSamples.mutateAsync({ speakerId });
      toast.success(t('voice-samples-cleared'));
    } catch (error) {
      toast.error(t(voiceErrorKey(error)));
    } finally {
      setConfirmClearAll(false);
    }
  };

  const describeConsent = (sample: VoiceProfileSample) => {
    const parts: string[] = [];
    if (sample.consent_recorded_at) {
      parts.push(
        t('voice-consent-recorded-on', {
          date: formatDate(sample.consent_recorded_at)
        })
      );
    }
    if (sample.consent_notice_version) {
      parts.push(
        t('voice-consent-notice-version', {
          version: sample.consent_notice_version
        })
      );
    }
    return parts.join(' · ');
  };

  // The summary line admins see: whether this person's voice is set up.
  const summaryText = () => {
    if (samples.isLoading) return t('loading');
    if (count === 0) return t('voice-no-voice-saved');
    return t('voice-voice-saved-count', { count });
  };
  const voiceSaved = count > 0;

  return (
    <section
      className="space-y-3 rounded-md border p-3"
      style={{ borderColor: getColour('border') }}
      aria-label={t('voice-samples')}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          {voiceSaved ? (
            <CheckCircle2 size={14} aria-hidden />
          ) : (
            <Fingerprint size={14} aria-hidden />
          )}
          <Text as="span" size="sm" weight="medium">
            {t('voice-samples')}
          </Text>
        </span>
        {/* The "N of 20" badge only earns its space when there's headroom to
            show. At zero it says the same thing as "No voice saved" below —
            drop it so the empty state carries one clear message, not two. */}
        {voiceSaved && (
          <Text as="span" size="xs" tone="muted">
            {t('voice-sample-count-of-max', { count, max: maxSamples })}
          </Text>
        )}
      </div>

      {/* One state line, understandable without any voice-profiles knowledge:
          "Voice saved · 2 samples" or "No voice saved". */}
      <Text as="p" size="sm">
        {summaryText()}
      </Text>

      {/* The how-to lives next to the status so a user who sees "No voice
          saved" learns in the same place that this is set up from a recording,
          not from this form. */}
      {!voiceSaved && (
        <Text as="p" size="xs" tone="muted">
          {t('voice-voice-saved-helper')}
        </Text>
      )}

      {/* The feature can be off while samples still exist from before; say so,
          and keep deletion available. */}
      {capabilities && !capabilities.enabled && (
        <Text as="p" size="xs" tone="muted">
          {t('voice-feature-disabled-samples-remain')}
        </Text>
      )}

      <ul className="space-y-2">
        {profiles.map(sample => (
          <li
            key={sample.id}
            className="flex items-start justify-between gap-3 rounded-md px-3 py-2"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="min-w-0">
              <Text as="p" size="sm">
                {sample.label || t('voice-sample-unlabelled')}
              </Text>
              <Text as="p" size="xs" tone="muted">
                {sample.created_at
                  ? t('voice-sample-added-on', {
                      date: formatDate(sample.created_at)
                    })
                  : null}
              </Text>
              {describeConsent(sample) && (
                <span className="flex items-center gap-1">
                  <ShieldCheck size={11} aria-hidden />
                  <Text as="span" size="xs" tone="muted">
                    {describeConsent(sample)}
                  </Text>
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              size="small"
              icon={<Trash2 width={12} />}
              label={t('delete')}
              onClick={() => setPendingDelete(sample)}
              disabled={deleteSample.isLoading}
            />
          </li>
        ))}
      </ul>

      {profiles.length > 0 && (
        <Button
          variant="secondary"
          size="small"
          label={t('voice-clear-all-samples')}
          onClick={() => setConfirmClearAll(true)}
          disabled={clearSamples.isLoading}
        />
      )}

      {/* Deleting one sample: names the sample, and says the others stay. */}
      <ConfirmationModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void handleDelete()}
        title={t('voice-delete-sample-title')}
        caption={t('voice-delete-sample-message', {
          sample: pendingDelete?.label || t('voice-sample-unlabelled'),
          name: speakerName,
          remaining: Math.max(0, count - 1)
        })}
        confirmLabel={t('delete')}
        confirmDisabled={deleteSample.isLoading}
        destructive
      />

      {/* Clearing all: a distinct confirmation, with the count spelled out. */}
      <ConfirmationModal
        isOpen={confirmClearAll}
        onClose={() => setConfirmClearAll(false)}
        onConfirm={() => void handleClearAll()}
        title={t('voice-clear-all-title')}
        caption={t('voice-clear-all-message', {
          count,
          name: speakerName
        })}
        confirmLabel={t('voice-clear-all-confirm')}
        confirmDisabled={clearSamples.isLoading}
        destructive
      />
    </section>
  );
};
